import type { RankItem } from '@/hooks/useSuperStats';

interface TopListCardProps {
  title: string;
  icon: string;
  iconClass?: string;
  items: RankItem[];
  loading: boolean;
  emptyText: string;
  valueSuffix?: string;
  /** Filtres optionnels affichés sous le titre (ex. par source de trafic). */
  filters?: { key: string; label: string }[];
  activeFilter?: string | null;
  onFilterChange?: (key: string | null) => void;
}

/**
 * Classement (sites / produits / services les plus visités). Liste scrollable
 * avec compteur mis en avant.
 */
export default function TopListCard({
  title,
  icon,
  iconClass = 'text-primary-500',
  items,
  loading,
  emptyText,
  valueSuffix = 'vues',
  filters,
  activeFilter = null,
  onFilterChange,
}: TopListCardProps) {
  const max = items[0]?.value || 1;

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 h-full flex flex-col">
      <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2 mb-3">
        <i className={`${icon} ${iconClass}`}></i>
        {title}
        {items.length > 0 && (
          <span className="ml-auto text-[11px] font-normal text-foreground-400">
            {items.length}
          </span>
        )}
      </h3>

      {filters && filters.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap mb-3">
          <button
            type="button"
            onClick={() => onFilterChange?.(null)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors cursor-pointer ${
              activeFilter == null
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-100 text-foreground-500 hover:bg-background-200/70'
            }`}
          >
            Toutes sources
          </button>
          {filters.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => onFilterChange?.(activeFilter === f.key ? null : f.key)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors cursor-pointer ${
                activeFilter === f.key
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-500 hover:bg-background-200/70'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-300"></i>
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-40 text-foreground-400">
          <i className={`${icon} text-3xl mb-2`}></i>
          <p className="text-sm text-center">{emptyText}</p>
        </div>
      ) : (
        <div className="space-y-2.5 max-h-80 overflow-y-auto pr-1">
          {items.map((item, index) => (
            <div
              key={item.id}
              className="relative flex items-center gap-3 px-3 py-2.5 rounded-lg bg-background-100"
            >
              <span className="w-6 h-6 rounded-full bg-secondary-100 text-secondary-900 flex items-center justify-center text-[11px] font-bold flex-shrink-0">
                {index + 1}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-foreground-900 truncate" title={item.title}>
                  {item.title}
                </div>
                <div className="text-[11px] text-foreground-400 truncate">{item.sub}</div>
                <div className="h-1 bg-background-200/70 rounded-full mt-1.5 overflow-hidden">
                  <div
                    className="h-full bg-primary-400 rounded-full"
                    style={{ width: `${(item.value / max) * 100}%` }}
                  />
                </div>
              </div>
              <span className="text-xs font-bold text-primary-700 bg-primary-100 rounded-full px-2.5 py-1 whitespace-nowrap flex-shrink-0">
                {item.value.toLocaleString('fr-FR')}
                <span className="hidden">{valueSuffix}</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}